import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getProfileSkillList,
  getProfileSkillById,
  createProfileSkill,
  updateProfileSkill,
  patchProfileSkill,
  deleteProfileSkill,
  deleteProfileSkillCategory,
} from "../api/skills.api";
import type {
  ProfileSkillPayload,
  ProfileSkillBulkPayload,
  GetProfileSkillListParams,
} from "@/interface/profile";
import { profileSkillKeys, candidateProfileKeys, STALE_5_MIN } from "@/lib/queryKey";
import type {
  GetCategoryListParams,
  GetSkillListParams,
} from "@/interface/masterManagement";
import { useInfiniteCategoryList } from "@/pages/master-management/hooks/useCategory";
import { useInfiniteSkillsByCategoryId } from "@/pages/master-management/hooks/useSkill";

export const useProfileSkillList = (params?: GetProfileSkillListParams) =>
  useQuery({
    queryKey: profileSkillKeys.list(params),
    queryFn: () => getProfileSkillList(params),
    ...STALE_5_MIN,
  });

export const useSkillCategories = (params?: GetCategoryListParams) =>
  useInfiniteCategoryList(params);

export const useSkillsByCategory = (
  categoryId?: number | string,
  params?: GetSkillListParams,
) => useInfiniteSkillsByCategoryId(categoryId, params);

export const useProfileSkillDetail = (id: number | string, enabled = true) =>
  useQuery({
    queryKey: profileSkillKeys.detail(id),
    queryFn: () => getProfileSkillById(id),
    enabled: Boolean(id) && enabled,
    ...STALE_5_MIN,
  });

export const useCreateProfileSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProfileSkillBulkPayload) =>
      createProfileSkill(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileSkillKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.skills() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useUpdateProfileSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProfileSkillBulkPayload) =>
      updateProfileSkill(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileSkillKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.skills() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const usePatchProfileSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<ProfileSkillPayload>;
    }) => patchProfileSkill(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: profileSkillKeys.lists() });
      queryClient.invalidateQueries({ queryKey: profileSkillKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.skills() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useDeleteProfileSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteProfileSkill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileSkillKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.skills() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useDeleteProfileSkillCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: number | string) =>
      deleteProfileSkillCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileSkillKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.skills() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};
